import { useContext } from 'react';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import Modal from '@ui/molecules/Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import Alert from '@ui/atoms/Alert';
import Stack from '@ui/layout/Stack';
import Text from '@ui/atoms/Text';
import Button from '@ui/atoms/Button';
import PricingContext from '@app/organisms/Pricing/PricingContext';
import { PRICING_NAME } from '@/utils/pricing';
import Link from '@ui/atoms/Link';
import useInternalRouteMapping from '@hooks/useInternalRouteMapping.hook';
import useChannelQuery from '@/queries/channel/useChannelQuery.hook';
import useArchivedShowsQuery from '@/queries/show/useArchivedShowsQuery.hook';
import { NUMBER_OF_ARCHIVED_SHOWS } from '@/utils/constants';
import Spinner from '@ui/atoms/Spinner';
import { IntercomAPI } from 'react-intercom';

const PricingErrorModal = ({ isOpened, onClose }) => {
    const { errorPayload } = useContext(PricingContext);
    const { link } = useInternalRouteMapping();
    const { data: channel } = useChannelQuery();
    const { data: archivedShows } = useArchivedShowsQuery(channel && channel[0].id);

    if (!errorPayload?.error?.response?.status) {
        return null;
    }

    const maximumArchivedShowsError = (
        <Stack $gap="0.25rem">
            <Text variant="headingM" fontWeight="--fw-bold" textAlign="center">
                <FormattedMessage
                    defaultMessage="Votre nombre d'émissions archivées dépasse la limite de l'offre {offer} ({shows}/{maxShows})"
                    values={{
                        offer: PRICING_NAME[errorPayload?.offer],
                        shows: archivedShows?.length,
                        maxShows: NUMBER_OF_ARCHIVED_SHOWS[errorPayload?.offer],
                    }}
                />
            </Text>
            <Text
                variant="bodyM"
                fontWeight="--fw-semibold"
                textAlign="center"
                color="--neutral500"
            >
                <FormattedMessage
                    defaultMessage="Veuillez en supprimer au moins <strong>{value}</strong> depuis le <link>sélecteur d'émission</link> pour continuer"
                    values={{
                        link: (chunk) => (
                            <Text
                                as={Link}
                                to={link('show.all')}
                                color="--primary"
                                fontWeight="--fw-bold"
                            >
                                {chunk}
                            </Text>
                        ),
                        strong: (chunk) => <strong>{chunk}</strong>,
                        value:
                            archivedShows?.length - NUMBER_OF_ARCHIVED_SHOWS[errorPayload?.offer],
                    }}
                />
            </Text>
        </Stack>
    );

    const billingError = (
        <Stack $gap="0.25rem">
            <Text variant="headingM" fontWeight="--fw-bold" textAlign="center">
                <FormattedMessage defaultMessage="Oups, il semble que vous ayez une erreur de facturation." />
            </Text>
            <Text
                variant="bodyM"
                fontWeight="--fw-semibold"
                textAlign="center"
                color="--neutral500"
            >
                <FormattedMessage
                    defaultMessage="Veuillez résoudre ce problème dans votre espace <link>Abonnement</link> avant de sélectionner une nouvelle offre. N'hésitez pas à <intercom>contacter notre équipe support</intercom> si besoin. 💌"
                    values={{
                        link: (chunk) => (
                            <Text
                                as={Link}
                                to={link('user.edit.show.tab', { tab: 'subscription' })}
                                color="--primary"
                                fontWeight="--fw-bold"
                            >
                                {chunk}
                            </Text>
                        ),
                        intercom: (chunk) => (
                            <Text
                                as={Link}
                                onClick={() => IntercomAPI('show')}
                                color="--primary"
                                fontWeight="--fw-bold"
                            >
                                {chunk}
                            </Text>
                        ),
                    }}
                />
            </Text>
        </Stack>
    );

    return (
        <Modal isOpen={isOpened} onClose={onClose} isDismissable={false}>
            {!archivedShows ? (
                <SpinnerWrapper>
                    <Spinner size={32} />
                </SpinnerWrapper>
            ) : (
                <Stack $gap="1.5rem" $align="center">
                    <IconAlert
                        variant="alert"
                        icon={
                            <FontAwesomeIcon
                                icon={icon({ name: 'circle-exclamation', style: 'solid' })}
                                size="xl"
                            />
                        }
                    />
                    {errorPayload.error.response.status === 422
                        ? maximumArchivedShowsError
                        : billingError}
                    <CloseButton variant="secondary" onPress={onClose}>
                        <FormattedMessage defaultMessage="Retour" />
                    </CloseButton>
                </Stack>
            )}
        </Modal>
    );
};

const SpinnerWrapper = styled.div`
    display: flex;
    justify-content: center;
`;

const IconAlert = styled(Alert)`
    color: var(--alert500);
    width: max-content;
`;

const CloseButton = styled(Button)`
    width: 100%;
`;

export default PricingErrorModal;
